import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import {
    FaSearch, FaBell, FaEnvelope, FaUsers, FaCheck,
    FaFilter, FaArrowLeft, FaUserPlus, FaCommentAlt,
    FaTimesCircle, FaTimes
} from 'react-icons/fa';
import PatientProfileView from '../components/PatientProfileView';
import FeedbackModal from '../components/shared/FeedbackModal';
import './ResearcherNetwork.css'; // Reusing same styling for consistency

const PatientNetwork = () => {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    const [activeTab, setActiveTab] = useState('solicitudes');
    const [searchQuery, setSearchQuery] = useState('');
    const [pendingList, setPendingList] = useState([]);
    const [contactList, setContactList] = useState([]);
    const [connectedSet, setConnectedSet] = useState(new Set());
    const [sentPendingSet, setSentPendingSet] = useState(new Set());

    // Profile View State
    const [viewingProfile, setViewingProfile] = useState(null);

    // Sugerencias reales
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [loadingRequests, setLoadingRequests] = useState(false);

    // Feedback Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });

    const showFeedback = (title, message, type = 'info') => {
        setModalConfig({ isOpen: true, title, message, type });
    };

    // Cargar datos reales
    useEffect(() => {
        const loadNetworkData = async () => {
            if (!token) return;
            try {
                setLoadingRequests(true);
                const [pending, contacts] = await Promise.all([
                    userService.getPendingConnections(token),
                    userService.getContacts(token)
                ]);

                // Mapear solicitudes pendientes al formato del UI
                const mappedPending = pending.map(req => ({
                    id: req.id,
                    userId: req.requester.id,
                    name: `${req.requester.profile?.firstName} ${req.requester.profile?.lastName}`,
                    role: 'Paciente',
                    img: req.requester.profile?.photoUrl
                        ? (req.requester.profile.photoUrl.startsWith('http') ? req.requester.profile.photoUrl : `${backendUrl.replace('/api', '')}${req.requester.profile.photoUrl}`)
                        : null,
                    avatar: req.requester.profile?.firstName?.charAt(0) || 'U'
                }));

                setPendingList(mappedPending);
                setContactList(contacts);

                // Marcar IDs de contactos existentes
                const contactIds = new Set(contacts.map(c => c.id));
                setConnectedSet(contactIds);

                // Cargar solicitudes enviadas (Pendientes)
                const sentPending = await userService.getSentPendingConnections(token);
                const sentIds = new Set(sentPending.map(req => req.recipient.id));
                setSentPendingSet(sentIds);
            } catch (err) {
                console.error('Error loading network data:', err);
            } finally {
                setLoadingRequests(false);
            }
        };

        loadNetworkData();
    }, [token, backendUrl]);

    // Función para manejar el inicio de chat
    const handleStartChat = (contact) => {
        navigate(`/chat?userId=${contact.id}`);
    };

    const handleViewProfile = async (patientId) => {
        setLoadingProfile(true);
        try {
            // Reusing getResearcherProfile as it's a generic detail fetcher by ID
            const detailedProfile = await userService.getResearcherProfile(patientId, token);
            setViewingProfile(detailedProfile);
        } catch (err) {
            console.error('Error loading detailed profile:', err);
        } finally {
            setLoadingProfile(false);
        }
    };

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (activeTab === 'sugerencias' && suggestions.length === 0) {
                setLoadingSuggestions(true);
                try {
                    const data = await userService.getPatients(token);
                    setSuggestions(data);
                } catch (err) {
                    console.error('Error fetching suggestions:', err);
                } finally {
                    setLoadingSuggestions(false);
                }
            }
        };
        if (token) fetchSuggestions();
    }, [activeTab, token, suggestions.length]);

    const acceptRequest = async (id) => {
        const acceptedReq = pendingList.find(r => r.id === id);
        try {
            await userService.respondToConnection(id, 'ACCEPTED', token);
            setPendingList(prev => prev.filter(r => r.id !== id));

            if (acceptedReq) {
                setConnectedSet(prev => {
                    const newSet = new Set(prev);
                    newSet.add(acceptedReq.userId);
                    return newSet;
                });
            }

            const contacts = await userService.getContacts(token);
            setContactList(contacts);

            showFeedback('¡Conectado!', 'Has aceptado la solicitud con éxito.', 'success');
        } catch (err) {
            console.error('Error accepting request:', err);
            showFeedback('Error', 'No se pudo aceptar la solicitud en este momento.', 'error');
        }
    };

    const rejectRequest = async (id) => {
        try {
            await userService.respondToConnection(id, 'REJECTED', token);
            setPendingList(prev => prev.filter(r => r.id !== id));
            showFeedback('Solicitud Rechazada', 'La solicitud ha sido eliminada correctamente.', 'info');
        } catch (err) {
            console.error('Error rejecting request:', err);
            showFeedback('Error', 'No se pudo rechazar la solicitud.', 'error');
        }
    };

    const toggleConnect = async (patientId) => {
        if (connectedSet.has(patientId) || sentPendingSet.has(patientId)) return;

        try {
            await userService.sendConnectionRequest(patientId, token);
            setSentPendingSet(prev => new Set(prev).add(patientId));
            showFeedback('¡Éxito!', 'Solicitud de conexión enviada correctamente.', 'success');
        } catch (err) {
            console.error('Error sending request:', err);
            const errorMsg = err.response?.data?.msg || 'No se pudo enviar la solicitud de conexión en este momento.';
            showFeedback('Error de Conexión', errorMsg, 'error');
        }
    };

    return (
        <div className="rn-page full-width">
            {/* ── TOP HEADER ── */}
            <header className="rn-top-header">
                <div className="rn-header-left">
                    <button className="rn-back-btn" onClick={() => navigate('/community-feed')}>
                        <FaArrowLeft /> Regresar
                    </button>
                    <div className="rn-header-logo">
                        <img src="/logoecseia.png" alt="Logo" className="rn-logo-img-small" />
                        <span className="rn-logo-text-small">
                            <span className="rn-eco">Eco</span>Science<strong>Innova</strong>
                        </span>
                    </div>
                </div>

                <div className="rn-header-center">
                    <div className="rn-search-bar">
                        <FaSearch className="rn-search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar en mi red..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div className="rn-header-right">
                    <div className="rn-header-actions">
                        <button className="rn-icon-btn"><FaBell /></button>
                        <button className="rn-icon-btn"><FaEnvelope /></button>
                    </div>
                    <div className="rn-user-identity">
                        <div className="rn-user-details">
                            <span className="rn-user-name-top">{user?.profile?.firstName} {user?.profile?.lastName}</span>
                            <span className="rn-user-role-top">Comunidad Paciente</span>
                        </div>
                        <div className="rn-user-avatar-top">
                            {user?.profile?.photoUrl ? (
                                <img
                                    src={user.profile.photoUrl.startsWith('http') ? user.profile.photoUrl : `${backendUrl.replace('/api', '')}${user.profile.photoUrl}`}
                                    alt="Avatar"
                                />
                            ) : (
                                <div className="rn-avatar-circle-top">
                                    {user?.profile?.firstName?.charAt(0) || 'U'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ── MAIN CONTAINER ── */}
            <div className="rn-main-container">
                <div className="rn-scroll">
                    <h1 className="rn-main-title">Mi Red de Apoyo</h1>

                    {/* ── TABS ── */}
                    <div className="rn-tabs">
                        <button
                            className={`rn-tab-btn ${activeTab === 'solicitudes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('solicitudes')}
                        >
                            <FaUserPlus /> Solicitudes {pendingList.length > 0 && <span className="rn-tab-count">{pendingList.length}</span>}
                        </button>
                        <button
                            className={`rn-tab-btn ${activeTab === 'sugerencias' ? 'active' : ''}`}
                            onClick={() => setActiveTab('sugerencias')}
                        >
                            ✨ Sugerencias
                        </button>
                        <button
                            className={`rn-tab-btn ${activeTab === 'contactos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('contactos')}
                        >
                            <FaUsers /> Contactos
                        </button>
                    </div>

                    {/* ── CONTENT SECTIONS ── */}
                    <div className="rn-content-grid">

                        {/* Solicitudes de conexión */}
                        {activeTab === 'solicitudes' && (
                            <section className="rn-section">
                                <div className="rn-section-header">
                                    <h2 className="rn-section-title"><FaUserPlus className="rn-title-icon" /> Solicitudes pendientes</h2>
                                </div>
                                <div className="rn-requests-list">
                                    {pendingList.map(req => (
                                        <div key={req.id} className="rn-request-row">
                                            <div className="rn-req-left">
                                                <div className="rn-req-avatar-wrap">
                                                    {req.img ? (
                                                        <img src={req.img} alt={req.name} className="rn-req-img" />
                                                    ) : (
                                                        <div className="rn-avatar-placeholder-small">{req.avatar}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    <h4>{req.name}</h4>
                                                    <p>{req.role} · {user?.profile?.country || 'Ubicación'}</p>
                                                </div>
                                            </div>
                                            <div className="rn-req-actions">
                                                <button
                                                    className="rn-btn-view-profile"
                                                    onClick={() => handleViewProfile(req.userId)}
                                                    disabled={loadingProfile}
                                                >
                                                    {loadingProfile ? '...' : 'Ver Perfil'}
                                                </button>
                                                <button className="rn-btn-approve" onClick={() => acceptRequest(req.id)}>
                                                    <FaCheck /> Aceptar
                                                </button>
                                                <button className="rn-btn-reject" onClick={() => rejectRequest(req.id)}>
                                                    <FaTimesCircle />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {pendingList.length === 0 && <p className="rn-empty-text">No tienes solicitudes pendientes.</p>}
                                </div>
                            </section>
                        )}

                        {/* Sugerencias */}
                        {activeTab === 'sugerencias' && (
                            <section className="rn-section">
                                <div className="rn-section-header">
                                    <h2 className="rn-section-title">✨ Personas que podrías conocer</h2>
                                </div>
                                <div className="rn-suggestions-grid">
                                    {loadingSuggestions ? (
                                        <p className="rn-empty-text">Cargando sugerencias...</p>
                                    ) : suggestions.length > 0 ? (
                                        suggestions.map(sug => {
                                            const fullName = `${sug.profile?.firstName || ''} ${sug.profile?.lastName || ''}`.trim() || 'Paciente';
                                            const initials = (sug.profile?.firstName?.charAt(0) || '') + (sug.profile?.lastName?.charAt(0) || '');
                                            const photoUrl = sug.profile?.photoUrl
                                                ? (sug.profile.photoUrl.startsWith('http') ? sug.profile.photoUrl : `${backendUrl.replace('/api', '')}${sug.profile.photoUrl}`)
                                                : null;

                                            return (
                                                <div key={sug.id} className="rn-suggestion-card-premium">
                                                    <div className="rn-sug-avatar-large">
                                                        <div className="rn-sug-img-border">
                                                            {photoUrl ? (
                                                                <img src={photoUrl} alt={fullName} />
                                                            ) : (
                                                                <div className="rn-avatar-placeholder-large">
                                                                    {initials || 'U'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <h3>{fullName}</h3>
                                                    <span className="rn-sug-type">Comunidad Paciente</span>
                                                    <p className="rn-sug-reason">
                                                        {sug.profile?.country || 'Sin ubicación'}
                                                    </p>
                                                    <button
                                                        className="rn-btn-outline-premium"
                                                        onClick={() => handleViewProfile(sug.id)}
                                                        disabled={loadingProfile}
                                                    >
                                                        {loadingProfile ? 'Cargando...' : 'Ver Perfil'}
                                                    </button>
                                                    <button
                                                        className={`rn-connect-btn ${connectedSet.has(sug.id) ? 'active' : (sentPendingSet.has(sug.id) ? 'pending' : '')}`}
                                                        onClick={() => toggleConnect(sug.id)}
                                                    >
                                                        {connectedSet.has(sug.id) ? 'Conectado' : (sentPendingSet.has(sug.id) ? 'Pendiente' : 'Conectar')}
                                                    </button>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="rn-empty-text">No hay sugerencias disponibles en este momento.</p>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Tus Contactos */}
                        {activeTab === 'contactos' && (
                            <section className="rn-section">
                                <div className="rn-section-header">
                                    <h2 className="rn-section-title"><FaUsers className="rn-title-icon" /> Tus Contactos ({contactList.length})</h2>
                                    <button className="rn-filter-btn-round"><FaFilter /> Filtrar</button>
                                </div>
                                <div className="rn-contacts-table-wrap">
                                    <table className="rn-contacts-table">
                                        <thead>
                                            <tr>
                                                <th>NOMBRE</th>
                                                <th>MÓDULO</th>
                                                <th>PAÍS</th>
                                                <th>ESTADO</th>
                                                <th>ACCIÓN</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contactList.map(contact => {
                                                const profile = contact.profile || {};
                                                const photo = profile.photoUrl
                                                    ? (profile.photoUrl.startsWith('http') ? profile.photoUrl : `${backendUrl.replace('/api', '')}${profile.photoUrl}`)
                                                    : null;

                                                return (
                                                    <tr key={contact.id}>
                                                        <td>
                                                            <div className="rn-td-user">
                                                                {photo ? (
                                                                    <img src={photo} alt={profile.firstName} />
                                                                ) : (
                                                                    <div className="rn-td-avatar-placeholder">
                                                                        {profile.firstName?.charAt(0) || 'U'}
                                                                    </div>
                                                                )}
                                                                <span>{profile.firstName} {profile.lastName}</span>
                                                            </div>
                                                        </td>
                                                        <td>Comunidad Paciente</td>
                                                        <td>{profile.country || 'N/A'}</td>
                                                        <td>
                                                            {(() => {
                                                                const lastActive = contact.lastActive ? new Date(contact.lastActive) : null;
                                                                const isOnline = lastActive && (new Date() - lastActive) < 5 * 60 * 1000;

                                                                return isOnline ? (
                                                                    <span className="rn-activity-badge online">● En línea</span>
                                                                ) : (
                                                                    <span className="rn-activity-badge offline">
                                                                        ● {lastActive ? `Visto ${new Date(lastActive).toLocaleDateString()}` : 'Desconectado'}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            <div className="rn-td-actions">
                                                                <button
                                                                    className="rn-btn-action view"
                                                                    title="Ver Perfil"
                                                                    onClick={() => handleViewProfile(contact.id)}
                                                                >
                                                                    <FaSearch />
                                                                </button>
                                                                <button
                                                                    className="rn-btn-action message"
                                                                    title="Chat"
                                                                    onClick={() => handleStartChat(contact)}
                                                                >
                                                                    <FaCommentAlt />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {contactList.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="rn-empty-td">Aún no tienes contactos en tu red.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                    </div>
                </div>
            </div>

            {/* ── MODAL: Ver Perfil ── */}
            {viewingProfile && (
                <div className="rn-modal-overlay" onClick={() => setViewingProfile(null)}>
                    <div className="rn-profile-modal" onClick={e => e.stopPropagation()}>
                        <button className="rn-modal-close-top" onClick={() => setViewingProfile(null)}><FaTimes /></button>
                        <PatientProfileView patient={viewingProfile} />
                    </div>
                </div>
            )}
            {/* Notification Modal */}
            <FeedbackModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
};

export default PatientNetwork;
